import type { Plugin } from "../core/plugin";
import { PluginRegistry } from "../core/pluginRegistry";

const ITEM_SELECTOR = ".core-dropdown__item, .coreDropdownItemV2";
const CONTENT_SELECTOR = ".core-dropdown__content, .coreDropdownContentV2";
const LABEL_SELECTOR =
  ".core-dropdown__content__label, .core-dropdown__label, .coreDropdownContentV2__label";

const ITEM_SELECTED_CLASS_V1 = "core-dropdown__item--state-selected";
const ITEM_SELECTED_CLASS_V2 = "coreDropdownItemV2--state-selectedStrong";
const CONTENT_SELECTED_CLASS_V1 = "core-dropdown__content--state-selected";
const CONTENT_DEFAULT_CLASS_V1 = "core-dropdown__content--state-default";
const CONTENT_SELECTED_CLASS_V2 = "coreDropdownContentV2--state-selectedStrong";
const CONTENT_DEFAULT_CLASS_V2 = "coreDropdownContentV2--state-default";

function isItemSelected(item: HTMLElement): boolean {
  return (
    item.classList.contains(ITEM_SELECTED_CLASS_V1) ||
    item.classList.contains(ITEM_SELECTED_CLASS_V2)
  );
}

export class CoreDropdown implements Plugin {
  private radioButtons: NodeListOf<HTMLInputElement>;
  private items: HTMLElement[] = [];
  private changeHandler: (e: Event) => void;
  private keydownHandler: (e: KeyboardEvent) => void;
  private clickHandler: (e: Event) => void;
  private activeIndex = -1;
  private isMultiple: boolean;

  constructor(private readonly element: HTMLElement) {
    this.radioButtons = this.element.querySelectorAll("input[type='radio']");
    this.isMultiple =
      this.element.getAttribute("data-selection-mode") === "multiple";
    this.changeHandler = (e) => this.handleChange(e);
    this.keydownHandler = (e) => this.handleKeydown(e);
    this.clickHandler = (e) => this.handleItemClick(e);
  }

  init(): void {
    this.items = Array.from(
      this.element.querySelectorAll<HTMLElement>(
        `${ITEM_SELECTOR}:not([aria-disabled='true'])`,
      ),
    );

    this.radioButtons.forEach((radio) => {
      radio.addEventListener("change", this.changeHandler);
      radio.addEventListener("click", this.changeHandler);
    });

    this.items.forEach((item, index) => {
      item.setAttribute("tabindex", index === 0 ? "0" : "-1");
      item.setAttribute("role", item.getAttribute("role") || "option");
      if (this.isMultiple) {
        item.setAttribute(
          "aria-selected",
          isItemSelected(item) ? "true" : "false",
        );
      }
    });

    if (!this.element.getAttribute("role")) {
      this.element.setAttribute("role", "listbox");
    }

    this.element.addEventListener("keydown", this.keydownHandler);

    // En selección múltiple el item es la unidad seleccionable: alterna su
    // propio estado "selected" (no usa radios/checkbox).
    if (this.isMultiple) {
      this.element.addEventListener("click", this.clickHandler);
    }
  }

  private handleKeydown(e: KeyboardEvent): void {
    const target = e.target as HTMLElement;
    const currentIndex = this.items.indexOf(
      target.closest(ITEM_SELECTOR) as HTMLElement,
    );

    if (currentIndex === -1) return;

    let nextIndex = currentIndex;

    switch (e.key) {
      case "ArrowDown":
      case "ArrowRight":
        nextIndex = (currentIndex + 1) % this.items.length;
        e.preventDefault();
        break;
      case "ArrowUp":
      case "ArrowLeft":
        nextIndex = (currentIndex - 1 + this.items.length) % this.items.length;
        e.preventDefault();
        break;
      case "Home":
        nextIndex = 0;
        e.preventDefault();
        break;
      case "End":
        nextIndex = this.items.length - 1;
        e.preventDefault();
        break;
      case "Enter":
      case " ":
        e.preventDefault();
        this.selectItem(currentIndex);
        return;
      default:
        return;
    }

    if (nextIndex !== currentIndex) {
      this.focusItem(nextIndex);
    }
  }

  private focusItem(index: number): void {
    this.items.forEach((item, i) => {
      item.setAttribute("tabindex", i === index ? "0" : "-1");
    });
    this.items[index].focus();
    this.activeIndex = index;
  }

  private selectItem(index: number): void {
    const item = this.items[index];
    if (!item) return;

    if (this.isMultiple) {
      this.toggleItemSelection(item);
      return;
    }

    const radio = item.querySelector<HTMLInputElement>("input[type='radio']");
    if (radio && !radio.disabled) {
      radio.checked = true;
      radio.dispatchEvent(new Event("change", { bubbles: true }));
      radio.dispatchEvent(new Event("click", { bubbles: true }));
      return;
    }

    const checkbox = item.querySelector<HTMLInputElement>(
      "input[type='checkbox']",
    );
    if (checkbox && !checkbox.disabled) {
      checkbox.checked = !checkbox.checked;
      checkbox.dispatchEvent(new Event("change", { bubbles: true }));
      return;
    }

    item.click();
  }

  private handleItemClick(e: Event): void {
    const target = e.target as HTMLElement;
    const item = target.closest<HTMLElement>(ITEM_SELECTOR);
    if (!item || !this.items.includes(item)) return;
    this.toggleItemSelection(item);
  }

  private toggleItemSelection(item: HTMLElement): void {
    const isV2 = item.classList.contains("coreDropdownItemV2");
    const itemSelectedClass = isV2
      ? ITEM_SELECTED_CLASS_V2
      : ITEM_SELECTED_CLASS_V1;
    const isSelected = item.classList.toggle(itemSelectedClass);
    item.setAttribute("aria-selected", isSelected ? "true" : "false");

    // Refleja el estado en el CoreDropdownContent (v1/V2) si existe, para que
    // reutilice su propio estilo "selected"/"selectedStrong".
    const content = item.querySelector<HTMLElement>(CONTENT_SELECTOR);
    if (content) {
      const contentIsV2 = content.classList.contains("coreDropdownContentV2");
      const selectedClass = contentIsV2
        ? CONTENT_SELECTED_CLASS_V2
        : CONTENT_SELECTED_CLASS_V1;
      const defaultClass = contentIsV2
        ? CONTENT_DEFAULT_CLASS_V2
        : CONTENT_DEFAULT_CLASS_V1;
      content.classList.toggle(selectedClass, isSelected);
      content.classList.toggle(defaultClass, !isSelected);
    }

    this.emitMultipleChange(item);
  }

  private getItemLabel(item: HTMLElement): string {
    return (
      item.getAttribute("data-label") ||
      item.querySelector(LABEL_SELECTOR)?.textContent?.trim() ||
      ""
    );
  }

  private getItemValue(item: HTMLElement): string {
    return item.getAttribute("data-value") || this.getItemLabel(item);
  }

  private emitMultipleChange(item: HTMLElement): void {
    const selectedItems = this.items.filter((i) => isItemSelected(i));

    this.element.dispatchEvent(
      new CustomEvent("coreDropdown:change", {
        bubbles: true,
        detail: {
          // Item que originó el cambio:
          value: this.getItemValue(item),
          label: this.getItemLabel(item),
          element: item,
          selected: isItemSelected(item),
          // Selección agregada:
          mode: "multiple",
          selectedValues: selectedItems.map((i) => this.getItemValue(i)),
          selectedLabels: selectedItems.map((i) => this.getItemLabel(i)),
        },
      }),
    );
  }

  private handleChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    const label =
      target.getAttribute("data-label") ||
      target.getAttribute("aria-label") ||
      target.value;
    this.element.dispatchEvent(
      new CustomEvent("coreDropdown:change", {
        bubbles: true,
        detail: {
          value: target.value,
          label: label,
          element: target,
        },
      }),
    );
  }

  destroy(): void {
    this.radioButtons.forEach((radio) => {
      radio.removeEventListener("change", this.changeHandler);
      radio.removeEventListener("click", this.changeHandler);
    });
    this.element.removeEventListener("keydown", this.keydownHandler);
    if (this.isMultiple) {
      this.element.removeEventListener("click", this.clickHandler);
    }
  }
}

PluginRegistry.register("coreDropdown", CoreDropdown);
